import { ReactNode, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

import { LinkButton } from 'components/Toolkit/Button/LinkButton';
import { media } from 'helpers/breakpoints';

export interface SimpleShowMoreInlineProps {
  visibleText: string;
  showMoreText?: string;
  hiddenText?: string;
  className?: string;
  children?: ReactNode;
  readMoreDataTracking?: string;
}

const SimpleShowMoreInlineContainer = styled.div<{
  showMore: boolean;
}>`
  display: grid;
  grid-template-rows: ${({ showMore }) =>
    showMore ? 'repeat(2, auto)' : 'auto'};
  grid-row-gap: ${({ theme }) => theme.spacing.S8};
  position: relative;
`;

const P = styled.p<{
  showMore?: boolean;
  useReadMoreButton?: boolean;
}>`
  ${({ theme }) => theme.fontSize.B14};
  display: inline-block;

  ${({ showMore, useReadMoreButton }) =>
    useReadMoreButton &&
    !showMore &&
    `
      position: relative;
      height: 100%;
      overflow: hidden;

      &:after {
        content: '';
        text-align: right;
        position: absolute;
        bottom: 0;
        right: 0;
        width: 25%;
        ${media.smallOnly}{
          width: 42%;
        }
        height: 1.6em;
        background: linear-gradient(
          to right,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 1) 45%
        );
      }
    `}

  ${({ showMore, useReadMoreButton }) =>
    !showMore && useReadMoreButton && `height: 5.2em; `}
`;

const SLinkButton = styled(LinkButton)<{ showLess?: boolean }>`
  position: ${({ showLess }) => (showLess ? '' : 'absolute')};
  bottom: 8px;
  right: 5px;
  height: 1.2em;
  margin-left: ${({ theme, showLess }) =>
    showLess ? `${theme.spacing.S4}` : ''};

  ${media.large} {
    right: 15px;
  }
`;

function SimpleShowMoreInline({
  visibleText,
  showMoreText = 'Show More',
  className,
  readMoreDataTracking = 'read-more',
  children,
}: SimpleShowMoreInlineProps) {
  const [showMore, setShowMore] = useState<boolean>(false);
  const [useReadMoreButton, setUseReadMoreButton] = useState<boolean>(false);

  // Determine if we're showing 3 lines or not.

  const textRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const height = textRef.current?.offsetHeight;
    if (height && height > 70) {
      setUseReadMoreButton(true);
    }
  }, [textRef]);

  return (
    <SimpleShowMoreInlineContainer showMore={showMore} className={className}>
      <P showMore={showMore} useReadMoreButton={useReadMoreButton}>
        <span ref={textRef}>{visibleText}</span>

        {children}

        {showMore && useReadMoreButton && (
          <SLinkButton
            ofType="SECONDARY"
            fontSize="B14"
            onClick={() => setShowMore(false)}
            showLess={true}
            underline
          >
            {' '}
            {'Show Less'}
          </SLinkButton>
        )}
      </P>

      {!showMore && useReadMoreButton && (
        <SLinkButton
          ofType="SECONDARY"
          fontSize="B14"
          onClick={() => setShowMore(true)}
          data-tracking={readMoreDataTracking}
          underline
        >
          {showMoreText}
        </SLinkButton>
      )}
    </SimpleShowMoreInlineContainer>
  );
}

export { SimpleShowMoreInline };
